import { Button } from '@/shared/components/ui/button'
import { Dialog, DialogContent } from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/shared/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Typography } from '@/shared/components/ui/typography'
import { isFreePlan } from '@/shared/utils/subscriptionPlan'
import { SubscriptionPlanType } from '@/types/subscription'
import { zodResolver } from '@hookform/resolvers/zod'
import LanguageOutlined from '@mui/icons-material/LanguageOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import { DialogProps } from '@radix-ui/react-dialog'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import * as z from 'zod'
import { MydraIcon } from '../../shared/components/MydraIcon'

const formSchema = z.object({
  visibility: z.enum(['public', 'private']),
})

type FormValues = z.infer<typeof formSchema>

type PlaybookVisibilityModalProps = Omit<DialogProps, 'defaultValues'> & {
  defaultValues?: {
    visibility: 'public' | 'private'
  }
  plan?: SubscriptionPlanType
  onSubmit: (values?: FormValues) => Promise<void>
}

export function PlaybookVisibilityModal({
  defaultValues,
  onSubmit,
  plan,
  ...props
}: PlaybookVisibilityModalProps) {
  const isDisabled = isFreePlan(plan)
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    disabled: isDisabled,
    defaultValues: {
      visibility: 'public',
      ...defaultValues,
    },
  })

  const {
    formState: { isSubmitting, isDirty },
    reset,
  } = form

  useEffect(() => {
    if (props.open) {
      reset({
        visibility: 'public',
        ...defaultValues,
      })
    }
  }, [defaultValues, reset, props.open])

  return (
    <Dialog {...props}>
      <DialogContent className="sm:max-w-96">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Typography variant="subtitle" className="font-medium">
              Project Visibility
            </Typography>
            {isDisabled && (
              <div className="flex items-center gap-1 rounded-lg bg-secondary px-2 py-1 text-sm font-bold text-white">
                <MydraIcon className="h-3 w-3" />
                Pro
              </div>
            )}
          </div>
          <Typography variant="body2" className="text-sm text-mydra-gray">
            {isDisabled ? (
              <>
                <Link
                  to={{
                    search: 'settings=plans',
                  }}
                  className="underline"
                >
                  Upgrade your plan
                </Link>{' '}
                to keep your playbook hidden and prevent others from seeing it.
              </>
            ) : (
              <>Easily switch between private and public visibility settings.</>
            )}
          </Typography>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select {...field} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">
                          <div className="flex items-center gap-2">
                            <LanguageOutlined sx={{ fontSize: 16 }} />
                            <span>Public</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="private">
                          <div className="flex items-center gap-2">
                            <LockOutlined sx={{ fontSize: 16 }} />
                            <span>Private</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange?.(false)}
                disabled={isSubmitting}
                className="w-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isDirty}
                className="w-full"
              >
                Update
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
